import React, { useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { CheckIcon } from 'lucide-react';
import { budgetRanges, projectTypes, timelines } from '../../data/contact';
import { site } from '../../data/site';
import { useContactBooking } from '../../contexts/ContactBookingContext';
const WEB3FORMS_ENDPOINT = 'https://api.web3forms.com/submit';
const WEB3FORMS_ACCESS_KEY = 'f7ac1f45-3214-4bcc-86e9-4903b6e38807';
type FormState = {
  name: string;
  email: string;
  company: string;
  website: string;
  projectType: string;
  budget: string;
  timeline: string;
  message: string;
};
type FormErrors = Partial<Record<keyof FormState, string>>;
const initialState: FormState = {
  name: '',
  email: '',
  company: '',
  website: '',
  projectType: '',
  budget: '',
  timeline: '',
  message: ''
};
const startOptions = [{
  id: 'enquiry-only',
  bookCall: false,
  title: 'Just send the enquiry',
  description: 'We reply within one business day with next steps.'
}, {
  id: 'enquiry-and-call',
  bookCall: true,
  title: 'Send it and book a call',
  description: '30 minutes on Google Meet. Pick a time right after you submit.'
}];
const fieldClasses = 'mt-2 h-12 w-full rounded-card border border-hairline bg-canvas px-4 text-small text-ink outline-none transition-colors duration-150 ease-out placeholder:text-muted focus-visible:border-ink focus-visible:ring-2 focus-visible:ring-accent/25';
const labelClasses = 'block text-small font-medium text-ink';
function Label({
  htmlFor,
  children,
  optional
}: {
  htmlFor: string;
  children: React.ReactNode;
  optional?: boolean;
}) {
  return <label htmlFor={htmlFor} className={labelClasses} data-id="element-301" data-mp="ts3rynkin31p">
      {children}
      {optional && <span className="ml-2 font-normal text-muted" data-id="element-302" data-mp="tug60pn5kt9h">optional</span>}
    </label>;
}
export function ContactForm() {
  const [values, setValues] = useState<FormState>(initialState);
  const [errors, setErrors] = useState<FormErrors>({});
  const [status, setStatus] = useState<'idle' | 'submitting' | 'sent' | 'error'>('idle');
  const [bookCall, setBookCall] = useState(false);
  const [modalDismissed, setModalDismissed] = useState(false);
  const {
    setLead,
    openModal,
    closeModal,
    isModalOpen,
    bookingConfirmed
  } = useContactBooking();
  const wasModalOpen = useRef(false);
  useEffect(() => {
    if (wasModalOpen.current && !isModalOpen && !bookingConfirmed) {
      setModalDismissed(true);
    }
    wasModalOpen.current = isModalOpen;
  }, [isModalOpen, bookingConfirmed]);
  const update = (field: keyof FormState) => (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setValues(current => ({
      ...current,
      [field]: event.target.value
    }));
    setErrors(current => ({
      ...current,
      [field]: undefined
    }));
  };
  const validate = (): FormErrors => {
    const next: FormErrors = {};
    if (!values.name.trim()) next.name = 'Please tell us your name.';
    if (!values.email.trim()) next.email = 'We need an email to reply to.';else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) next.email = 'That email does not look right.';
    if (!values.company.trim()) next.company = 'Which company is this for?';
    if (!values.projectType) next.projectType = 'Pick the closest project type.';
    if (!values.budget) next.budget = 'A range is enough — "Not sure yet" is fine.';
    if (!values.timeline) next.timeline = 'When would you want this live?';
    if (values.message.trim().length < 20) next.message = 'A couple of sentences about the company helps us be useful.';
    return next;
  };
  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const nextErrors = validate();
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      const firstField = Object.keys(nextErrors)[0];
      document.getElementById(firstField)?.focus();
      return;
    }
    setStatus('submitting');
    try {
      const response = await fetch(WEB3FORMS_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          access_key: WEB3FORMS_ACCESS_KEY,
          subject: 'New project enquiry — ShreyasStudio',
          name: values.name,
          email: values.email,
          company: values.company,
          website: values.website,
          project_type: values.projectType,
          budget_range: values.budget,
          timeline: values.timeline,
          message: values.message,
          wants_call: bookCall ? 'yes' : 'no',
          botcheck: ''
        })
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        console.error('Web3Forms submission failed', {
          response,
          data
        });
        setStatus('error');
        return;
      }
    } catch (error) {
      console.error('Web3Forms submission failed', error);
      setStatus('error');
      return;
    }
    setLead({
      name: values.name,
      email: values.email,
      company: values.company,
      message: values.message
    });
    setModalDismissed(false);
    setStatus('sent');
    if (bookCall) {
      window.setTimeout(() => openModal('prefill'), 600);
    }
  };
  if (status === 'sent') {
    return <div role="status" className="min-h-[420px] rounded-card border border-hairline bg-surface p-8 lg:p-10" data-id="element-303" data-mp="1xg3cyphcqnz5">
        <span className="flex h-10 w-10 items-center justify-center rounded-full bg-ink" data-id="element-304" data-mp="1sjcu5ue5xea1">
          <CheckIcon size={18} aria-hidden="true" className="text-white" data-id="element-305" data-mp="1nhnj4eqxx0ly" />
        </span>

        {bookingConfirmed ? <>
            <h2 className="mt-6 text-[24px] leading-[1.25] tracking-[-0.03em] text-ink" data-id="element-306" data-mp="1eiablrmdmm3v">
              You&rsquo;re booked.
            </h2>
            <p className="mt-4 max-w-[42ch] text-[15px] leading-6 text-[#6E6E6E]" data-id="element-307" data-mp="3cwlng944yzsb">
              Calendar invite and Google Meet link are on their way to your inbox.
            </p>
            <Link to="/" className="mt-8 inline-flex text-small text-accent underline decoration-accent/30 underline-offset-4 transition-colors duration-150 ease-out hover:decoration-accent" data-id="element-308" data-mp="169e7mt8mxqeh">
              Back to home
            </Link>
          </> : <>
            <h2 className="mt-6 max-w-[22ch] text-[28px] leading-[1.15] tracking-[-0.03em] text-ink" data-id="element-309" data-mp="11bcsuki8sd8t">
              Thanks — we&rsquo;ve got it.
            </h2>

            {bookCall ? <>
                <p className="mt-4 max-w-[42ch] text-body text-muted" data-id="element-310" data-mp="1typ43yiycymt">
                  Now pick a time that suits you.
                </p>

                {modalDismissed && <div className="mt-8" data-id="element-311" data-mp="1kxhd81irn02r">
                    <button type="button" onClick={() => openModal('prefill')} className="flex h-12 w-full cursor-pointer items-center justify-center rounded-card border border-ink bg-ink px-5 text-small font-medium text-white outline-none transition-colors duration-150 ease-out hover:border-[#262626] hover:bg-[#262626] focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2" data-id="element-312" data-mp="1l0df20xqi8wh">
                      Pick a time
                    </button>
                    <p className="mt-4 text-[13px] text-[#6E6E6E]" data-id="element-313" data-mp="62hji1ljhvap">
                      No rush — we&rsquo;ll also reply by email.
                    </p>
                  </div>}
              </> : <>
                <p className="mt-4 max-w-[42ch] text-body text-muted" data-id="element-314" data-mp="39gbuazwnrxrn">
                  We reply within one working day.
                </p>

                <div className="mt-8 border-t border-hairline pt-8" data-id="element-315" data-mp="3jg2of0hb4ar7">
                  <p className="text-[13px] text-[#6E6E6E]" data-id="element-316" data-mp="2vqrx09lujspq">
                    Changed your mind? Book a call instead.{' '}
                    <button type="button" onClick={() => openModal('prefill')} className="cursor-pointer text-accent underline decoration-accent/30 underline-offset-4 transition-colors duration-150 ease-out hover:decoration-accent" data-id="element-317" data-mp="2qsgmdwnq9e6g">
                      Pick a time
                    </button>
                  </p>
                </div>
              </>}
          </>}
      </div>;
  }
  return <form noValidate onSubmit={handleSubmit} data-id="element-318" data-mp="2o66mwhzzil0e">
      <h2 id="contact-form-title" className="text-[24px] tracking-[-0.02em] text-ink" data-id="element-319" data-mp="2o5dny379ozst">
        Start a project
      </h2>
      <p className="mt-4 max-w-[46ch] text-small text-muted" data-id="element-320" data-mp="2xxb13fhu61u6">
        Eight fields. The two dropdowns are the ones that matter — they let us come to the call with
        a real recommendation.
      </p>

      <div className="mt-10 grid gap-6 sm:grid-cols-2" data-id="element-321" data-mp="37vgxxioschs6">
        <div data-id="element-322" data-mp="37x1v7g2hietq">
          <Label htmlFor="name" data-id="element-323" data-mp="37te04che5kcw">Name</Label>
          <input id="name" name="name" type="text" autoComplete="name" value={values.name} onChange={update('name')} aria-invalid={Boolean(errors.name)} aria-describedby={errors.name ? 'name-error' : undefined} className={fieldClasses} placeholder="Ananya Iyer" data-id="element-324" data-mp="3bw0uol5g5jds" />
          {errors.name && <p id="name-error" className="mt-2 text-[13px] text-ink" data-id="element-325" data-mp="mb8ti86e3tk3">
              {errors.name}
            </p>}
        </div>

        <div data-id="element-326" data-mp="1eyd36qgfgw27">
          <Label htmlFor="email" data-id="element-327" data-mp="1eyldqbp2vpa8">Email</Label>
          <input id="email" name="email" type="email" autoComplete="email" value={values.email} onChange={update('email')} aria-invalid={Boolean(errors.email)} aria-describedby={errors.email ? 'email-error' : undefined} className={fieldClasses} placeholder="ananya@company.in" data-id="element-328" data-mp="1eyv80bh5oilq" />
          {errors.email && <p id="email-error" className="mt-2 text-[13px] text-ink" data-id="element-329" data-mp="2gwuy8ovdz8d6">
              {errors.email}
            </p>}
        </div>

        <div data-id="element-330" data-mp="2bytb9suuaowf">
          <Label htmlFor="company" data-id="element-331" data-mp="2bzb8ost8n95x">Company</Label>
          <input id="company" name="company" type="text" autoComplete="organization" value={values.company} onChange={update('company')} aria-invalid={Boolean(errors.company)} aria-describedby={errors.company ? 'company-error' : undefined} className={fieldClasses} placeholder="Bhoomi Coffee Roasters" data-id="element-332" data-mp="2qur7tc8odv96" />
          {errors.company && <p id="company-error" className="mt-2 text-[13px] text-ink" data-id="element-333" data-mp="39nl953ddloux">
              {errors.company}
            </p>}
        </div>

        <div data-id="element-334" data-mp="3ilf52d78aghk">
          <Label htmlFor="website" optional data-id="element-335" data-mp="3in8cvvtkv6r5">
            Current website
          </Label>
          <input id="website" name="website" type="url" autoComplete="url" value={values.website} onChange={update('website')} className={fieldClasses} placeholder="bhoomicoffee.in" data-id="element-336" data-mp="3eleig59z6yzf" />
        </div>

        <div data-id="element-337" data-mp="3o75xfnvqw615">
          <Label htmlFor="projectType" data-id="element-338" data-mp="3o7fqjkc36tb2">Project type</Label>
          <select id="projectType" name="projectType" value={values.projectType} onChange={update('projectType')} aria-invalid={Boolean(errors.projectType)} aria-describedby={errors.projectType ? 'projectType-error' : undefined} className={`${fieldClasses} cursor-pointer ${values.projectType ? 'text-ink' : 'text-muted'}`} data-id="element-339" data-mp="3t724jvzqodkx">
            <option value="" data-id="element-340" data-mp="39kcqp70ak8kk">Select one</option>
            {projectTypes.map(type => <option key={type} value={type} data-id="element-341" data-mp="2vja3us1h2u8l">
                {type}
              </option>)}
          </select>
          {errors.projectType && <p id="projectType-error" className="mt-2 text-[13px] text-ink" data-id="element-342" data-mp="30kha2mnyvld5">
              {errors.projectType}
            </p>}
        </div>

        <div data-id="element-343" data-mp="11awgs47bi5ts">
          <Label htmlFor="budget" data-id="element-344" data-mp="11a4zlyegwan1">Budget range</Label>
          <select id="budget" name="budget" value={values.budget} onChange={update('budget')} aria-invalid={Boolean(errors.budget)} aria-describedby={errors.budget ? 'budget-error' : 'budget-hint'} className={`${fieldClasses} cursor-pointer ${values.budget ? 'text-ink' : 'text-muted'}`} data-id="element-345" data-mp="x9kzqn2iesx7">
            <option value="" data-id="element-346" data-mp="1g0m09fja2wiv">Select one</option>
            {budgetRanges.map(range => <option key={range} value={range} data-id="element-347" data-mp="1b1hpwlkmasqy">
                {range}
              </option>)}
          </select>
          {errors.budget ? <p id="budget-error" className="mt-2 text-[13px] text-ink" data-id="element-348" data-mp="4n0apt9iaswt">
              {errors.budget}
            </p> : <p id="budget-hint" className="mt-2 text-[13px] text-muted" data-id="element-349" data-mp="3vu2zyf5sap4e">
              Projects start at ₹ 15,000. A range keeps the first call useful.
            </p>}
        </div>

        <div className="sm:col-span-2" data-id="element-350" data-mp="3qkjt4spl3hdh">
          <Label htmlFor="timeline" data-id="element-351" data-mp="3vlpluqj8v4n0">Timeline</Label>
          <select id="timeline" name="timeline" value={values.timeline} onChange={update('timeline')} aria-invalid={Boolean(errors.timeline)} aria-describedby={errors.timeline ? 'timeline-error' : undefined} className={`${fieldClasses} cursor-pointer ${values.timeline ? 'text-ink' : 'text-muted'}`} data-id="element-352" data-mp="3vjwf7b8mskf0">
            <option value="" data-id="element-353" data-mp="i501tz8h4912">Select one</option>
            {timelines.map(option => <option key={option} value={option} data-id="element-354" data-mp="n5eb1klt9p0o">
                {option}
              </option>)}
          </select>
          {errors.timeline && <p id="timeline-error" className="mt-2 text-[13px] text-ink" data-id="element-355" data-mp="2oykdmzgez6sl">
              {errors.timeline}
            </p>}
        </div>

        <div className="sm:col-span-2" data-id="element-356" data-mp="jb37f44y3c2m">
          <Label htmlFor="message" data-id="element-357" data-mp="eayprasawbat">What are you trying to sell?</Label>
          <textarea id="message" name="message" rows={6} value={values.message} onChange={update('message')} aria-invalid={Boolean(errors.message)} aria-describedby={errors.message ? 'message-error' : undefined} className="mt-2 w-full rounded-card border border-hairline bg-canvas px-4 py-3 text-small leading-6 text-ink outline-none transition-colors duration-150 ease-out placeholder:text-muted focus-visible:border-ink focus-visible:ring-2 focus-visible:ring-accent/25" placeholder="A paragraph about the company, who buys from you, and what the site needs to do differently." data-id="element-358" data-mp="s9yc2bscce3t" />
          {errors.message && <p id="message-error" className="mt-2 text-[13px] text-ink" data-id="element-359" data-mp="381ujjsg08zun">
              {errors.message}
            </p>}
        </div>

        <fieldset className="sm:col-span-2" data-id="element-360" data-mp="1ngxmlih0zy2a">
          <legend className={labelClasses} data-id="element-361" data-mp="1sa0hpv1gbfkl">How would you like to start?</legend>
          <div className="mt-2 grid gap-4 sm:grid-cols-2" data-id="element-362" data-mp="1eilx5jrwb6bf">
            {startOptions.map(option => {
            const isSelected = bookCall === option.bookCall;
            return <label key={option.id} className={`relative cursor-pointer rounded-card p-5 transition-[border-color,background-color] duration-200 ease-out ${isSelected ? 'border-2 border-ink bg-[#F6F6F4]' : 'border border-hairline bg-white'}`} data-id="element-363" data-mp="277191xfqd8ij">
                  <input type="radio" name="start-preference" value={option.id} checked={isSelected} onChange={() => setBookCall(option.bookCall)} className="peer sr-only" data-id="element-364" data-mp="226vb5zivaps0" />
                  <span aria-hidden="true" className={`absolute right-5 top-5 h-4 w-4 rounded-full border transition-colors duration-200 ease-out peer-focus-visible:ring-2 peer-focus-visible:ring-accent peer-focus-visible:ring-offset-2 ${isSelected ? 'border-ink bg-ink' : 'border-hairline bg-white'}`} data-id="element-365" data-mp="6q57m5n5dkpd" />
                  <span className="block max-w-[24ch] text-[15px] font-medium leading-5 text-ink" data-id="element-366" data-mp="3k6uqgtm6ma9q">
                    {option.title}
                  </span>
                  <span className="mt-2 block max-w-[32ch] text-[13px] leading-5 text-[#6E6E6E]" data-id="element-367" data-mp="3b43biju3kwdc">
                    {option.description}
                  </span>
                </label>;
          })}
          </div>
        </fieldset>
      </div>

      <button type="submit" disabled={status === 'submitting'} className="mt-10 inline-flex h-12 w-full cursor-pointer items-center justify-center rounded-card border border-ink bg-ink px-6 text-small font-medium text-white outline-none transition-colors duration-150 ease-out hover:bg-[#262626] hover:border-[#262626] focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 disabled:opacity-60" data-id="element-368" data-mp="19b5q392nh62f">
        {status === 'submitting' ? 'Sending…' : bookCall ? 'Send enquiry & pick a time' : 'Send enquiry'}
      </button>

      {status === 'error' ? <p role="alert" className="mt-5 text-[13px] text-ink" data-id="element-369" data-mp="xxdisk88pg7p">
          That did not send. Try again, or email us directly at{' '}
          <a href={`mailto:${site.email}`} className="text-accent underline decoration-accent/30 underline-offset-4 transition-colors duration-150 ease-out hover:decoration-accent" data-id="element-370" data-mp="12vyh2e3xlpti">
            {site.email}
          </a>
          .
        </p> : <p className="mt-5 text-[13px] text-muted" data-id="element-371" data-mp="2icfdnztyyvr4">
          We reply within one business day. Your details are never shared or added to a mailing list.
        </p>}
    </form>;
}